import { useMemo, useState } from 'react';
import { FormField } from 'ui/formField';
import { BasicFilterSelect } from 'ui/selects';
import { Link } from 'ui/links';

import { displayContent } from './content';
import { SettingsReadOnlyText } from './EditorContentContainer.styled';
import { useEditableFieldsContext } from '../context/EditableFieldsContext';
import { useQueryParamContext } from '../context/QueryParamContext';
import { useMessageTypesQueryContext } from '../context/MessageTypesQueryContext';
import { useTemplateQueryContext } from '../context/TemplateQueryContext';
import { formatMessageTypes, getIsEditMode } from './utils';

/*
  A component used across every template type to select
  the message type that is associated with the template.
  The Aurora-styled counterpart to the one in the templateEdit module
*/
export const MessageTypeSelect = ({ isEditable = true }) => {
  const { view } = useQueryParamContext();
  const { messageTypes } = useMessageTypesQueryContext(); // defaults to [] until populated
  const { isEditMode, liveTemplate, setCurrentTemplateField } = useEditableFieldsContext();
  const { medium } = useTemplateQueryContext();
  const [searchTerm, setSearchTerm] = useState<string>('');

  const options = formatMessageTypes(messageTypes);
  const defaultValues = liveTemplate.messageType?.id ? [liveTemplate.messageType.id] : [];
  const isEditModeVisible = getIsEditMode(view, isEditMode) && isEditable;

  const memoizedSelectedOptions = useMemo(() => {
    return searchTerm
      ? options.filter(
          ({ id, label, doubleLine }) =>
            label.toLowerCase().includes(searchTerm.toLowerCase()) ||
            id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            doubleLine?.toLowerCase().includes(searchTerm.toLowerCase()),
        )
      : options;
  }, [options, searchTerm]);

  const messageTypeHelp = (
    <>
      {'Determines unsubscribe eligibility. Visit Settings > '}
      <Link
        size="caption"
        href={`/settings/channels/${medium}`}
        target="_blank"
        rel="noopener noreferrer"
        useReactRouter
      >
        Message Channels and Types
      </Link>
      {' to manage and add message types.'}
    </>
  );

  return (
    <FormField
      helpText={isEditModeVisible ? messageTypeHelp : null}
      isHorizontalLayout
      labelText={displayContent.labelMessageType}
    >
      {isEditModeVisible ? (
        <BasicFilterSelect
          data-test="settings-select-message-type"
          data-testid="message-type-field"
          defaultValues={defaultValues}
          id="settings-select-message-type"
          onSelect={([newMessageTypeId]) =>
            setCurrentTemplateField('messageType', {
              id: newMessageTypeId,
              name:
                messageTypes?.find((messageType) => messageType.id === newMessageTypeId)?.name ||
                newMessageTypeId,
            })
          }
          options={memoizedSelectedOptions}
          onSearchChange={(searchedItem) => setSearchTerm(searchedItem ?? '')}
          disableInternalFiltering
          isFullWidth
        />
      ) : (
        <SettingsReadOnlyText data-test="msg-type-ro">
          {liveTemplate.messageType?.name || ''}
        </SettingsReadOnlyText>
      )}
    </FormField>
  );
};
